import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router';

import { Heading1 } from '@components/commons';
import GalleryLayout from '@components/Gallery/GalleryLayout';
import Image from '@components/Image';
import LUXE_CITY from '@data/series/luxe_city';
import MUSEUM from '@data/series/museum';
import { Photo, Series } from '@types';
import { formatStartEndDates } from '@utils/formatters';
import { getStartEndDates, StartEndDates } from '@utils/getters';
import { isSeriesCover } from '@utils/index';
import { sortSeriesByDates, sortSeriesByNbPhotos } from '@utils/sort';
import { DataContext } from '@utils/useData';
import { IntlContext } from '@utils/useIntl';

const HIGHLIGHTS = [MUSEUM.id, LUXE_CITY.id];

const Series = () => {
  const { photos, series } = useContext(DataContext);
  const { locale, translateTrad } = useContext(IntlContext);

  const displayedSeries = series.filter(({ location }) => !location);

  const photosBySeries = photos.reduce<Record<string, Photo[]>>(
    (data, photo) => {
      photo.seriesIds?.forEach((seriesId) => {
        if (displayedSeries.find(({ id }) => seriesId === id)) {
          if (data[seriesId]) data[seriesId].push(photo);
          else data[seriesId] = [photo];
        }
      });
      return data;
    },
    {},
  );
  const datesBySeries = Object.keys(photosBySeries).reduce<
    Record<string, StartEndDates>
  >((data, id) => {
    const seriesPhotos = photosBySeries[id];
    return { ...data, [id]: getStartEndDates(seriesPhotos) };
  }, {});

  const filteredSeries = displayedSeries.filter(
    ({ id }) => !!photosBySeries[id],
  );

  const renderSeries = (series: Series) => {
    const seriesPhotos = photosBySeries[series.id];
    const nbPhotos = seriesPhotos.length;
    if (!nbPhotos) return null;

    const { start, end } = getStartEndDates(seriesPhotos);

    const coverPhoto =
      seriesPhotos.find(isSeriesCover(series)) || seriesPhotos[0];

    return (
      <Link key={series.id} to={`/series/${series.id}`}>
        <Image
          photo={coverPhoto}
          label={translateTrad(series.name)}
          // labelSub={`${nbPhotos} ${nbPhotos > 1 ? 'photos' : 'photo'}`}
          labelSub={formatStartEndDates(start, end, locale)}
        />
      </Link>
    );
  };

  return (
    <div className="desktop:py-12">
      <Helmet>
        <title>Marc Malignan | Series</title>
      </Helmet>
      <Heading1 className="desktop:hidden mb-10 text-center">Series</Heading1>
      <div className="desktop:flex desktop:gap-20">
        <section className="grow basis-0">
          <GalleryLayout
            gap="gap-x-4 gap-y-8"
            nbPerRowDesktop={2}
            nbPerRowTablet={3}
            nbPerRowPhone={2}
            elements={filteredSeries
              .filter(({ id }) => HIGHLIGHTS.includes(id))
              .sort(sortSeriesByNbPhotos(photosBySeries))}
            renderElement={renderSeries}
          />
        </section>
        <section className="grow basis-0 max-desktop:mt-8">
          <GalleryLayout
            className="mb-8 desktop:justify-start"
            gap="gap-x-4 gap-y-8"
            nbPerRowDesktop={3}
            nbPerRowTablet={4}
            nbPerRowPhone={2}
            elements={filteredSeries
              .filter(({ id }) => !HIGHLIGHTS.includes(id))
              // .sort(sortSeriesByNbPhotos(photosBySeries))}
              .sort(sortSeriesByDates(datesBySeries, photosBySeries))}
            renderElement={renderSeries}
          />
        </section>
      </div>
    </div>
  );
};

export default Series;
